import { useState, useEffect, useRef, useCallback } from "react";
import "./StockList.css";

const BASE = "https://cubixweberp.com:213/api";
const PAGE_SIZE = 250;

function countUrl(company, category, q) {
  return `${BASE}/cpayscount/${company}/ITEMCOUNT/${category}/${encodeURIComponent(
    q
  )}`;
}

function listUrl(company, category, q, page, locationParameter) {

  let locationParameterToPass = locationParameter ? locationParameter : "-";

  return `${BASE}/CRMStockListPage/${company}/${category}/${encodeURIComponent(q)}/${locationParameterToPass}/-/${page}/${PAGE_SIZE}`;
}

function formatHeader(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const DEFAULT_HIDDEN_COLUMNS = [
  "image",
  "img",
  "photo",
  "picture",
  "itemimage",
  "itemimg",
  "imagepath",
  "imageurl",
  "imgurl",
  "picpath",
];

function isNumericColumn(rows, key) {
  let sawValue = false;
  for (const r of rows) {
    const v = r[key];
    if (v === null || v === undefined || v === "") continue;
    sawValue = true;
    if (isNaN(Number(v))) return false;
  }
  return sawValue;
}

export default function StockListMain({
  company = "SUNRISEDXB",
  category = "UNIVERSAL",
  apiLocationList,
  hiddenColumns = DEFAULT_HIDDEN_COLUMNS,
  selectedBranchLocation
}) {

 
  const [query, setQuery] = useState("-");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countLoading, setCountLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageJump, setPageJump] = useState("1");
  const [expandedCells, setExpandedCells] = useState(() => new Set());

  const requestToken = useRef(0);
  const debounceTimer = useRef(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const loadCount = useCallback(
    async (q, token) => {
      setCountLoading(true);
      try {
        const res = await fetch(countUrl(company, category, q));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (token !== requestToken.current) return;
        const cnt =
          Array.isArray(data) && data[0] && typeof data[0].CNT !== "undefined"
            ? Number(data[0].CNT)
            : 0;
        setTotalCount(cnt);
      } catch (err) {
        if (token !== requestToken.current) return;
        setTotalCount(0);
      } finally {
        if (token === requestToken.current) setCountLoading(false);
      }
    },
    [company, category]
  );

  const loadList = useCallback(
    async (q, p, token, loc) => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(listUrl(company, category, q, p, loc));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (token !== requestToken.current) return;
        setRows(Array.isArray(data) ? data : []);
        setExpandedCells(new Set());
      } catch (err) {
        if (token !== requestToken.current) return;
        setError(
          `Couldn't load stock list. ${err.message}. Check the API is reachable and try again.`
        );
        setRows([]);
      } finally {
        if (token === requestToken.current) setLoading(false);
      }
    },
    [company, category]
  );

  useEffect(() => {
    // Wait until we have a location to filter by (either from
    // apiLocationList's first entry, or "-" for no location filter)
    // before making any API calls.
    if (selectedBranchLocation === null) return;

    const token = ++requestToken.current;
    setPageJump(String(page));
    loadCount(query, token);
    loadList(query, page, token, selectedBranchLocation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, page, selectedBranchLocation]);

 

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setPage(1);
      setQuery(val.trim() === "" ? "-" : val.trim());
    }, 350);
  };

  const handleClear = () => {
    clearTimeout(debounceTimer.current);
    setSearchInput("");
    setPage(1);
    setQuery("-");
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    setPage(p);
  };

  const handlePageJumpKeyDown = (e) => {
    if (e.key === "Enter") {
      let p = parseInt(pageJump, 10);
      if (isNaN(p)) return;
      p = Math.min(Math.max(p, 1), totalPages);
      goToPage(p);
    }
  };

  const isDescriptionColumn = (key) => /desc|name|remark|narration/i.test(key);

  const toggleExpand = (cellKey) => {
    setExpandedCells((prev) => {
      const next = new Set(prev);
      if (next.has(cellKey)) {
        next.delete(cellKey);
      } else {
        next.add(cellKey);
      }
      return next;
    });
  };

  const hiddenSet = new Set(hiddenColumns.map((c) => c.toLowerCase()));
  const columns =
    rows.length > 0
      ? Object.keys(rows[0]).filter((k) => !hiddenSet.has(k.toLowerCase()))
      : [];
  const numericCols = {};
  columns.forEach((k) => (numericCols[k] = isNumericColumn(rows, k)));

  const pageButtons = (() => {
    const windowSize = 1;
    const pages = new Set([1, totalPages]);
    for (let p = page - windowSize; p <= page + windowSize; p++) {
      if (p >= 1 && p <= totalPages) pages.add(p);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const p of sorted) {
      if (prev && p - prev > 1) out.push({ ellipsis: true, key: `e${p}` });
      out.push({ page: p, key: p });
      prev = p;
    }
    return out;
  })();

  return (
    <div className="stocklist-wrap">
      <header className="stocklist-header">
        <h1 className="stocklist-title">Stock list</h1>
   

        <div className="stocklist-toolbar">
          <div className="stocklist-search-box">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="stocklist-search-icon"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search item code, description..."
              autoComplete="off"
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>

          {searchInput && (
            <button className="stocklist-clear-btn" onClick={handleClear}>
              Clear
            </button>
          )}

         

          <div className="stocklist-count-badge">
            <span className={`num ${countLoading ? "updating" : ""}`}>
              {totalCount.toLocaleString()}
            </span>
            <span className="label">{totalCount === 1 ? "item" : "items"}</span>
          </div>
        </div>
      </header>

      <div className="stocklist-table-container">
        <div className="stocklist-scroll-x">
          <table>
            {columns.length > 0 && !error && (
              <thead>
                <tr>
                  {columns.map((k) => (
                    <th
                      key={k}
                      style={{ textAlign: numericCols[k] ? "right" : "left" }}
                    >
                      {formatHeader(k)}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {error ? (
                <tr>
                  <td>
                    <div className="stocklist-error-state">{error}</div>
                  </td>
                </tr>
              ) : loading ? (
                <tr className="loading-row">
                  <td colSpan={columns.length || 10}>
                    <span className="spinner" />
                    Loading stock...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td>
                    <div className="stocklist-empty-state">
                      <svg
                        className="icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      No items match this search.
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={i}>
                    {columns.map((k) => {
                      const v = row[k];
                      const display =
                        v === null || v === undefined || v === "" ? "—" : v;
                      const isDesc = isDescriptionColumn(k);
                      const cellKey = `${i}-${k}`;
                      const isExpanded = expandedCells.has(cellKey);
                      return (
                        <td
                          key={k}
                          className={[
                            numericCols[k] ? "num-cell" : "",
                            isDesc ? "desc-cell" : "",
                            isDesc && isExpanded ? "expanded" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          title={isDesc ? String(display) : undefined}
                          onClick={
                            isDesc ? () => toggleExpand(cellKey) : undefined
                          }
                        >
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <footer className="stocklist-footer">
        <div className="stocklist-page-info">
          Page {page} of {totalPages} &middot; {totalCount.toLocaleString()}{" "}
          items
        </div>

        <div className="stocklist-pagination">
          <button
            disabled={page <= 1}
            onClick={() => goToPage(page - 1)}
            aria-label="Previous page"
          >
            &larr;
          </button>
          {pageButtons.map((b) =>
            b.ellipsis ? (
              <span className="ellipsis" key={b.key}>
                &hellip;
              </span>
            ) : (
              <button
                key={b.key}
                className={b.page === page ? "current" : ""}
                onClick={() => goToPage(b.page)}
              >
                {b.page}
              </button>
            )
          )}
          <button
            disabled={page >= totalPages}
            onClick={() => goToPage(page + 1)}
            aria-label="Next page"
          >
            &rarr;
          </button>
        </div>

        <div className="stocklist-page-jump">
          <span>Go to</span>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={pageJump}
            onChange={(e) => setPageJump(e.target.value)}
            onKeyDown={handlePageJumpKeyDown}
          />
        </div>
      </footer>
    </div>
  );
}
